import debug from 'debug'
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import {
  type AppRouteHandle,
  useLocation,
  useMatches,
} from 'src/utils/router/route'
import { trackPageRender } from 'src/utils/tracker'
import { isPathnameClaimed } from './pageRenderClaim'

const log = debug('pixai:render-tracker')

/**
 * Automatic page_render tracker placed in Root.tsx.
 *
 * Fires a `page_render` event after mount + rAF for every navigation,
 * UNLESS the pathname has been "claimed" by a page calling
 * `useRenderTracker({ ready })`.
 *
 * Must be placed as a sibling AFTER `<Outlet />` in Root.tsx's render tree
 * to ensure correct effect ordering (page effects run first).
 */
export function RenderTracker() {
  const location = useLocation()
  const matches = useMatches<unknown, AppRouteHandle>()
  const params = useParams()
  const pathname = location.pathname
  const leafHandle = matches.at(-1)?.handle
  const pageName = leafHandle?.name

  const firedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pageName) {
      log('no pageName for pathname %s — is handle.name configured?', pathname)
      return
    }
    if (firedForRef.current === pathname) return
    if (isPathnameClaimed(pathname)) {
      log('skipping auto-track for %s (claimed by page component)', pathname)
      return
    }

    const rafId = requestAnimationFrame(() => {
      // Double-check after rAF as a safety net
      if (firedForRef.current === pathname) return
      if (isPathnameClaimed(pathname)) {
        log('skipping auto-track for %s (claimed after rAF)', pathname)
        return
      }

      firedForRef.current = pathname
      log('firing page_render for page %s (auto mode)', pageName)
      trackPageRender({ location, matches, params, pageName })
    })

    return () => cancelAnimationFrame(rafId)

    // and params always change together with pathname, which is the true trigger.
  }, [pathname, pageName])

  return null
}
